<!-- 导航组件：左侧边栏布局 -->
<template>
  <div class="nav-container">
    <!-- 左侧边栏 -->
    <div class="sidebar">
      <!-- 系统标题 -->
      <div class="sidebar-header">
        <div class="system-logo">
          <el-icon><Monitor /></el-icon>
        </div>
        <h1 class="system-title">康养紧急呼叫系统</h1>
      </div>

      <!-- 导航菜单 -->
      <nav class="sidebar-nav">
        <el-menu
          :default-active="$route.path"
          class="nav-menu"
          router
          background-color="transparent"
          text-color="#1f2d3d"
          active-text-color="#0b6b88"
        >
          <el-menu-item
            v-for="item in menuItems"
            :key="item.path"
            :index="item.path"
          >
            <el-icon>
              <DataBoard v-if="item.path === '/dashboard'" />
              <Monitor v-else-if="item.path === '/deviceManagement'" />
              <Phone v-else-if="item.path === '/callRecords'" />
              <Upload v-else-if="item.path === '/ota'" />
              <CreditCard v-else-if="item.path === '/simCardList'" />
              <Microphone v-else-if="item.path === '/recordingManagement'" />
              <DataAnalysis v-else-if="item.path === '/rtcStats'" />
              <Box v-else-if="item.path === '/packageManagement'" />
              <OfficeBuilding v-else-if="item.path === '/tenantManagement'" />
              <Key v-else-if="item.path === '/tenantApiKeys'" />
            </el-icon>
            <span>{{ item.name }}</span>
          </el-menu-item>
        </el-menu>
      </nav>

      <!-- 数据源切换 -->
      <!-- 
      <div class="sidebar-datasource">
        <DataSourceSwitch :show-description="false" />
      </div>
      -->

      <!-- 用户信息 -->
      <div class="sidebar-footer">
        <div class="user-info">
          <div class="user-avatar">
            <el-icon><User /></el-icon>
          </div>
          <div class="user-details">
            <div class="username">{{ userDisplayName }}</div>
            <el-dropdown trigger="click" @command="handleCommand">
              <span class="user-actions">
                <el-icon><Setting /></el-icon>
              </span>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="profile">
                    <el-icon><User /></el-icon>
                    个人资料
                  </el-dropdown-item>
                  <el-dropdown-item v-if="showUserManage" command="users">
                    <el-icon><Setting /></el-icon>
                    用户管理
                  </el-dropdown-item>
                  <el-dropdown-item divided command="logout">
                    <el-icon><SwitchButton /></el-icon>
                    退出登录
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </div>
      </div>
    </div>

    <!-- 主内容区域 -->
    <main class="main-content">
      <router-view :key="$route.fullPath"></router-view>
    </main>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import * as ElementPlusIconsVue from "@element-plus/icons-vue";
const {
  Monitor,
  DataBoard,
  Setting,
  Phone,
  User,
  SwitchButton,
  Upload,
  CreditCard,
  Microphone,
  DataAnalysis,
  Box,
  OfficeBuilding,
  Key,
} = ElementPlusIconsVue as any;
import { ElMessageBox, ElMessage } from "element-plus";
import router from "../router/index";
import jsCookie from "js-cookie";
import config from "@/config";
import { useStore } from "vuex";
import websocketManager from "@/services/websocketManager";
import { NAVIGATION_MENU_ITEMS } from "@/types/permission";
import { smartLoginCheck, startSilentTokenRefresh } from "@/utils/auth";
// import DataSourceSwitch from '../components/DataSourceSwitch.vue';

// 中文注释：引入全局store
const store = useStore();

// 用户信息（从store获取）
const userInfo = computed(() => store.getters.getUser || { username: "admin" });
// 中文注释：优先展示显示名称，其次用用户名
const userDisplayName = computed(() => {
  const u = userInfo.value as any;
  return u?.display_name || u?.username || "admin";
});

// 获取最新的用户信息
onMounted(async () => {
  // 智能登录检查（支持静默token刷新）
  if (config.requireLogin) {
    const canProceed = smartLoginCheck();
    if (!canProceed) return;

    // 启动静默token刷新
    startSilentTokenRefresh();
  }

  try {
    await store.dispatch("fetchUserInfo");
  } catch (error) {
    console.error("获取用户信息失败:", error);
    // 如果获取失败，可能是token过期，跳转到登录页
    router.push("/login");
  }
});
const userRole = computed(() => store.getters.getUserRole);
const showUserManage = computed(() => {
  const role = userRole.value;
  return role === "admin" || role === "super_admin";
});

// 使用集中化的菜单项配置
const allMenuItems = NAVIGATION_MENU_ITEMS;

// 计算当前用户可见菜单项
const menuItems = computed(() => {
  // 中文注释：调试用户角色和菜单过滤逻辑
  console.log("当前用户角色:", userRole.value);
  console.log("所有菜单项:", allMenuItems);

  // 如果用户角色为空，返回所有菜单项（临时容错）
  if (!userRole.value) {
    console.warn("用户角色为空，显示所有菜单项");
    return allMenuItems;
  }

  const filteredItems = allMenuItems.filter((item) =>
    item.roles.includes(userRole.value)
  );
  console.log("过滤后的菜单项:", filteredItems);
  return filteredItems;
});

// 处理下拉菜单命令
const handleCommand = (command: string) => {
  switch (command) {
    case "profile":
      router.push("/profile");
      break;
    case "users":
      router.push("/users");
      break;
    case "logout":
      loginOut();
      break;
  }
};

// 退出系统
const loginOut = () => {
  websocketManager.disconnect(); // 登出时断开 WebSocket
  if (!config.requireLogin) {
    return;
  }
  ElMessageBox.confirm("确定要退出系统吗？", "确认退出", {
    confirmButtonText: "确定",
    cancelButtonText: "取消",
    type: "warning",
  }).then(() => {
    jsCookie.remove("userToken");
    jsCookie.remove("username");
    store.dispatch("logout");
    window.dispatchEvent(new CustomEvent("user-logout"));
    ElMessage.success("退出成功");
    router.push("/login");
  });
};
</script>

<style lang="scss" scoped>
.nav-container {
  height: 100vh;
  display: flex;
  background-color: #f5f7fa;
}

// 左侧边栏样式
.sidebar {
  width: 256px;
  background: linear-gradient(180deg, #9ed6e3 0%, #7fcfe0 100%);
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  position: relative;
  z-index: 1000;
}

// 系统标题区域
.sidebar-header {
  padding: 24px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.system-logo {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3498db, #2980b9);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.system-title {
  color: #1f2d3d;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
}

// 导航菜单
.sidebar-nav {
  flex: 1;
  padding: 20px 0;
}

.nav-menu {
  border: none;
}

.nav-menu .el-menu-item {
  height: 50px;
  line-height: 50px;
  margin: 4px 16px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.nav-menu .el-menu-item:hover {
  background-color: rgba(11, 107, 136, 0.12);
  color: #0b6b88;
}

.nav-menu .el-menu-item.is-active {
  background-color: rgba(11, 107, 136, 0.18);
  color: #0b6b88;
  font-weight: 600;
}

.nav-menu .el-menu-item .el-icon {
  margin-right: 12px;
  font-size: 18px;
}

// 数据源切换区域
.sidebar-datasource {
  padding: 16px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

// 用户信息区域
.sidebar-footer {
  padding: 16px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
}

.user-details {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.username {
  color: #1f2d3d;
  font-size: 14px;
  font-weight: 500;
}

.user-actions {
  color: #1f2d3d;
  cursor: pointer;
  transition: color 0.3s ease;
}

.user-actions:hover {
  color: #1f2d3d;
}

// 主内容区域
.main-content {
  flex: 1;
  overflow-y: auto;
  background-color: #f5f7fa;
}

// 响应式设计
@media (max-width: 768px) {
  .sidebar {
    width: 200px;
  }

  .system-title {
    font-size: 14px;
  }

  .nav-menu .el-menu-item {
    margin: 2px 8px;
    height: 44px;
    line-height: 44px;
  }

  .nav-menu .el-menu-item .el-icon {
    margin-right: 8px;
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .sidebar {
    width: 180px;
  }

  .system-title {
    font-size: 12px;
  }

  .nav-menu .el-menu-item {
    margin: 2px 4px;
    height: 40px;
    line-height: 40px;
    font-size: 12px;
  }

  .nav-menu .el-menu-item .el-icon {
    margin-right: 6px;
    font-size: 14px;
  }
}
</style>
